import { token } from '@/infrastructure/container';
import type { ILogger } from './Logger';

// `localStorage`, wrapped. A store may not touch it directly (INVARIANTS rule 6's platform ban),
// and the recipe is rule 12's: interface, token, plain class, fake in src/testing/fakes.ts.
//
// One thing lives in here today and it is deliberately small: the project id the admin device
// created, so a reload finds the same grading day rather than starting a second one. No bearer key
// ever goes in. The keys are born on the admin laptop and reach the wall as pixels; a key in
// storage is a key that survives the day and outlives the codes it was minted with (docs/sync.md).
//
// No `start()`: this is a synchronous accessor with no event source, so there is nothing to arm and
// nothing to publish. It is the one shape of platform wrapper rule 12's three properties do not
// apply to in full, and saying so is cheaper than pretending it has a lifecycle.

export interface IDomStorage {
  get(key: string): string | undefined;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export const TOKEN_IDomStorage = token<IDomStorage>('IDomStorage');

// Private browsing, a disabled-storage policy and a full quota all throw rather than return false.
// A grading day that cannot remember its project id still has to run, so every call degrades to a
// logged miss instead of taking the screen down.
export class LocalDomStorage implements IDomStorage {
  constructor(private readonly logger: ILogger) {}

  get(key: string): string | undefined {
    try {
      return localStorage.getItem(key) ?? undefined;
    } catch (cause) {
      this.logger.warn('storage read failed', key, cause);
      return undefined;
    }
  }

  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (cause) {
      this.logger.warn('storage write failed', key, cause);
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (cause) {
      this.logger.warn('storage remove failed', key, cause);
    }
  }
}
